import { Intent } from '@blueprintjs/core';
import { Button } from 'components/buttons';
import { FormNumericInput } from 'components/form-fields';
import { Modal, ModalSize } from 'components/notifications';
import { useValidation } from 'hooks/useValidation';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { HelperFunctions } from 'helpers';

const defaultObject = {};

export function OcrVarianceTableForm(
    {
        varianceData = defaultObject,
        onValidationUpdate = null,
        calculateVariance,
        dataType,
        modalOpen,
        onClose,
        onFinish
    }
) {
    const percentageMaxValue = 100;
    const decimalMaxValue = 1000000;

    const [varianceDataValue, setVarianceData] = useState(null);

    const [isValid, errors, validate] = useValidation();

    useEffect(() => {
        if (varianceData) {
            setVarianceData({ ...varianceData });
        }
    }, [varianceData, modalOpen]);

    useEffect(() => {
        validateVarianceData();
    }, [varianceDataValue]);

    useEffect(() => {
        HelperFunctions.executeIfNotNull(onValidationUpdate, isValid);
    }, [isValid]);

    function validateVarianceData() {
        if (varianceDataValue) {
            const rules = getRules();
            validate(rules, varianceDataValue);
        }
    }

    function getRules() {
        const rules = [];

        if (varianceData) {
            if (varianceData.previousEditable) {
                rules.push({ fieldName: 'previousVisit', required: true });
            }

            if (varianceData.currentEditable) {
                rules.push({ fieldName: 'currentVisit', required: true });
            }
        }

        return rules;
    }

    function selectPreviousVisitValue(previousVisit) {
        if (previousVisit === '') {
            previousVisit = null;
        }

        const varianceDataClone = { ...varianceDataValue };
        varianceDataClone.previousVisit = previousVisit;

        setVarianceData(varianceDataClone);
    }

    function selectCurrentVisitValue(currentVisit) {
        if (currentVisit === '') {
            currentVisit = null;
        }

        const varianceDataClone = { ...varianceDataValue };
        varianceDataClone.currentVisit = currentVisit;

        setVarianceData(varianceDataClone);
    }

    return (
        <Fragment>
            <Modal
                isOpen={modalOpen}
                title={'Edit variance data'}
                onClose={onClose}
                size={ModalSize.SMALL}
            >
                {varianceDataValue && <div>
                    <FormNumericInput
                        disabled={!varianceDataValue.currentEditable}
                        onValueChange={selectCurrentVisitValue}
                        selectedValue={varianceDataValue.currentVisit}
                        max={dataType.toUpperCase() === 'PERCENTAGE' ? percentageMaxValue : decimalMaxValue}
                        min={0}
                        allowDecimal={dataType.toUpperCase() === 'DECIMAL' || dataType.toUpperCase() === 'POUND'}
                        headingText={varianceData.context ? 'Current ' + varianceData.context : 'Current visit'}
                        dangerHelperText={errors.currentVisit}
                    />
                    <FormNumericInput
                        disabled={!varianceDataValue.previousEditable}
                        onValueChange={selectPreviousVisitValue}
                        selectedValue={varianceDataValue.previousVisit}
                        max={dataType.toUpperCase() === 'PERCENTAGE' ? percentageMaxValue : decimalMaxValue}
                        min={0}
                        allowDecimal={dataType.toUpperCase() === 'DECIMAL' || dataType.toUpperCase() === 'POUND'}
                        headingText={varianceData.context ? 'Previous ' + varianceData.context : 'Previous visit'}
                        dangerHelperText={errors.previousVisit}
                    />
                    <FormNumericInput
                        disabled={true}
                        onValueChange={() => null}
                        selectedValue={calculateVariance(varianceDataValue)}
                        max={dataType.toUpperCase() === 'PERCENTAGE' ? percentageMaxValue : decimalMaxValue}
                        allowDecimal={dataType.toUpperCase() === 'DECIMAL' || dataType.toUpperCase() === 'POUND'}
                        headingText='Variance'
                    />
                </div>}
                <div className="bp6-dialog-footer">
                    <div className="bp6-dialog-footer-actions">
                        <Button text="Edit" intent={Intent.PRIMARY} onClick={() => onFinish(varianceDataValue)} disabled={!isValid}></Button>
                        <Button text="Cancel" onClick={onClose}></Button>
                    </div>
                </div>
            </Modal>
        </Fragment>
    );
}


OcrVarianceTableForm.propTypes = {
    varianceData: PropTypes.object,
    modalOpen: PropTypes.bool.isRequired,
    onFinish: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    calculateVariance: PropTypes.func.isRequired,
    dataType: PropTypes.string.isRequired,
    onValidationUpdate: PropTypes.func
};